import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { SelectorOverlay } from '../common';
import { destinationService, addressSearchService } from '../../api/services';
import { useDeepLTranslated } from '../../hooks/useDeepLTranslated';

/**
 * 스키장 선택 — SelectorOverlay 기반
 */
export default function SkiResortSelector({ value, onChange, placeholder, className = '', variant = 'default' }) {
  const { t } = useTranslation();
  const effectivePlaceholder = placeholder || t('form.select_ski_resort');
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [rawResorts, setRawResorts] = useState([]);
  // 추천 OFF 스키장 — 기본 화면엔 미노출, 검색어 입력 시에만 결과에 합류.
  const [unrecommendedResorts, setUnrecommendedResorts] = useState([]);

  useEffect(() => {
    let cancelled = false;
    destinationService.getSkiResorts().then((data) => {
      if (cancelled) return;
      // BugHerd #99 follow-up: 어드민 [추천 스키장] 토글 OFF 항목은 사용자 사이트 기본 노출 차단.
      // Cluster A: 운영자가 ADMIN 등록 후 추천 체크를 잊은 케이스 보호 — 검색어 입력 시
      // 추천 OFF 도 결과에 합류 (기본 노출 정책은 그대로 유지).
      const all = data || [];
      const cleaned = all.filter((r) => r.is_recommended === true);
      const unrec = all.filter((r) => r.is_recommended === false);
      setRawResorts(cleaned.map((r) => ({ value: String(r.id), label: r.name, address: r.address })));
      setUnrecommendedResorts(unrec.map((r) => ({ value: String(r.id), label: r.name, address: r.address })));
    }).catch((e) => console.warn(e.message));
    return () => { cancelled = true; };
  }, []);

  // Row 30: 스키장 라벨/주소 render-time 번역 (AirportSelector와 동일 패턴)
  //   useDeepLTranslated는 실패 시 원문 유지 + 캐시 자동 활용으로 새로고침 시 즉시 반영
  const labelBundle = useMemo(() => rawResorts.map((r) => r.label), [rawResorts]);
  const addressBundle = useMemo(() => rawResorts.map((r) => r.address || ''), [rawResorts]);
  const tLabels = useDeepLTranslated(labelBundle);
  const tAddresses = useDeepLTranslated(addressBundle);
  const resorts = useMemo(
    () => rawResorts.map((r, i) => ({
      ...r,
      nameKo: r.label, // #217: BE Ski.name 매칭용 원본 한글명 보존 (label 은 표시용 번역본)
      label: tLabels[i] || r.label,
      address: tAddresses[i] || r.address,
    })),
    [rawResorts, tLabels, tAddresses]
  );

  // 추천 OFF 도 검색 풀에 합류 — 기본 화면(no query)에는 미노출.
  const unrecResortsTranslated = useMemo(
    () => unrecommendedResorts.map((r) => ({ ...r, nameKo: r.label })),
    [unrecommendedResorts]
  );
  const filteredResorts = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return resorts;
    const pool = [...resorts, ...unrecResortsTranslated];
    return pool.filter((r) => r.label.toLowerCase().includes(q));
  }, [search, resorts, unrecResortsTranslated]);

  const displayLabel = useMemo(() => {
    if (!value) return null;
    // #169: 추천 ON + 추천 OFF(검색 합류) 풀 모두에서 조회.
    //       못 찾아도 raw id(value)를 절대 노출하지 않음(null → placeholder).
    const found = [...resorts, ...unrecResortsTranslated].find((r) => r.value === value);
    return found ? found.label : null;
  }, [value, resorts, unrecResortsTranslated]);

  const handleOpen = () => {
    if (!open) setSearch('');
    setOpen((v) => !v);
  };

  const handleSelect = async (resort) => {
    const label = resort.label || resort.value;
    // #217: 4번째 인자 nameKo(원본 한글명) 전달 → BE Ski.name 매칭. label(번역본)은 표시 전용.
    const nameKo = resort.nameKo || resort.label || resort.value;
    if (resort.address) {
      onChange && onChange(resort.value, label, resort.address, nameKo);
    } else {
      onChange && onChange(resort.value, label, '', nameKo);
      try {
        const results = await addressSearchService.search(nameKo);
        const match = results.find((r) => r.name.includes(nameKo.replace(/리조트|스키장/g, '').trim().slice(0, 3)));
        if (match) onChange(resort.value, label, match.address, nameKo);
      } catch {}
    }
    setOpen(false);
  };

  return (
    <SelectorOverlay
      displayLabel={displayLabel}
      placeholder={effectivePlaceholder}
      className={className}
      variant={variant}
      open={open}
      onOpen={handleOpen}
      onClose={() => setOpen(false)}
      maxWidth="520px"
      search={search}
      onSearchChange={(e) => setSearch(e.target.value)}
      searchPlaceholder={t('form.select_ski_resort')}
      title={t('common.main_ski_quick')}
    >
      <div className="px-6 pb-6">
        {!search.trim() && (
          resorts.length === 0 ? (
            <p className="text-sm text-mono-400 text-center py-8">{t('common.no_search_results')}</p>
          ) : (
            <div className="grid grid-cols-2 gap-x-4 gap-y-1">
              {resorts.map((resort) => {
                const isSelected = resort.value === value;
                return (
                  <button
                    key={resort.value}
                    type="button"
                    onClick={() => handleSelect(resort)}
                    className={`text-left px-3 py-2.5 rounded-lg text-sm transition-colors ${
                      isSelected
                        ? 'text-orange-500 font-semibold bg-orange-50'
                        : 'text-mono-700 hover:bg-mono-50 hover:text-mono-900'
                    }`}
                  >
                    {resort.label}
                  </button>
                );
              })}
            </div>
          )
        )}

        {search.trim() && (
          <>
            {filteredResorts.length > 0 ? (
              <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                {filteredResorts.map((resort) => {
                  const isSelected = resort.value === value;
                  return (
                    <button
                      key={resort.value}
                      type="button"
                      onClick={() => handleSelect(resort)}
                      className={`text-left px-3 py-2.5 rounded-lg text-sm transition-colors ${
                        isSelected
                          ? 'text-orange-500 font-semibold bg-orange-50'
                          : 'text-mono-700 hover:bg-mono-50 hover:text-mono-900'
                      }`}
                    >
                      {resort.label}
                    </button>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-mono-400 text-center py-8">{t('common.no_search_results')}</p>
            )}
          </>
        )}
      </div>
    </SelectorOverlay>
  );
}
